/* ==========================================================================
   Alwayz Clean Lab - bundle.js
   <bundle-builder>: pick products, watch the kit fill up, see tiered savings
   live, add everything to the cart in one request.
   The discount itself must exist in Shopify (automatic discount or code).
   The theme only displays the estimate and optionally applies a code.
   ========================================================================== */
(() => {
  'use strict';
  const A = window.Alwayz;
  const { $, $$, define, formatMoneyShort, toast } = A;

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.items = new Map(); // variant id -> { qty, price, title, image }
      this.t1 = { qty: +this.dataset.tier1Qty, pct: +this.dataset.tier1Pct };
      this.t2 = { qty: +this.dataset.tier2Qty, pct: +this.dataset.tier2Pct };
      this.slotsEl = $$('[data-slot]', this);
      this.addBtn = $('[data-kit-add]', this);
      this.clearBtn = $('[data-kit-clear]', this);

      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-kit-item]');
        if (card && e.target.closest('[data-kit-plus]')) this.change(card, 1);
        else if (card && e.target.closest('[data-kit-minus]')) this.change(card, -1);
        else if (e.target.closest('[data-kit-clear]')) this.clear();
        else if (e.target.closest('[data-kit-add]')) this.submit();
      });
      this.render();
    }
    get count() { let n = 0; this.items.forEach((i) => { n += i.qty; }); return n; }
    change(card, delta) {
      const id = card.dataset.id;
      const cur = this.items.get(id) || { qty: 0, price: +card.dataset.price, compare: +card.dataset.compare || 0, title: card.dataset.title, image: card.dataset.image };
      const max = this.slotsEl.length;
      if (delta > 0 && this.count >= max) { toast(`Your kit holds ${max} items`); return; }
      cur.qty = Math.max(0, cur.qty + delta);
      if (cur.qty === 0) this.items.delete(id); else this.items.set(id, cur);
      this.render();
    }
    clear() { this.items.clear(); this.render(); }
    discountPct() { const n = this.count; return n >= this.t2.qty ? this.t2.pct : n >= this.t1.qty ? this.t1.pct : 0; }
    render() {
      const n = this.count;
      let subtotal = 0; // items at sale price, before the kit discount
      let mrp = 0;      // items at compare-at price (falls back to sale price)
      this.items.forEach((i) => { subtotal += i.price * i.qty; mrp += Math.max(i.compare, i.price) * i.qty; });
      const pct = this.discountPct();
      const disc = Math.round((subtotal * pct) / 100);

      // cards
      $$('[data-kit-item]', this).forEach((card) => {
        const it = this.items.get(card.dataset.id);
        const q = it ? it.qty : 0;
        card.classList.toggle('is-picked', q > 0);
        const badge = $('[data-kit-count]', card);
        badge.hidden = q === 0;
        badge.textContent = q;
        $('[data-kit-minus]', card).disabled = q === 0;
      });

      // slots
      const flat = [];
      this.items.forEach((i) => { for (let k = 0; k < i.qty; k++) flat.push(i); });
      this.slotsEl.forEach((slot, idx) => {
        const it = flat[idx];
        slot.classList.toggle('is-filled', !!it);
        slot.innerHTML = it && it.image ? `<img src="${it.image}" alt="">` : '';
      });

      // progress + hint
      const max = this.slotsEl.length;
      $('[data-kit-bar]', this).parentElement.style.setProperty('--p', (n / max).toFixed(3));
      $('[data-kit-bar]', this).style.setProperty('--p', (n / max).toFixed(3));
      const hint = $('[data-kit-hint]', this);
      if (n === 0) hint.textContent = `Add ${this.t1.qty} items to save ${this.t1.pct}%.`;
      else if (n < this.t1.qty) { const need = this.t1.qty - n; hint.textContent = `Add ${need} more item${need > 1 ? 's' : ''} to save ${this.t1.pct}%.`; }
      else if (n < this.t2.qty) { const need = this.t2.qty - n; hint.textContent = `${this.t1.pct}% off unlocked! Add ${need} more to save ${this.t2.pct}%.`; }
      else hint.textContent = `Maximum savings unlocked: ${this.t2.pct}% off your kit.`;

      // totals: MRP, then sale price (subtotal), then the kit discount, then total
      $('[data-kit-mrp-row]', this).hidden = mrp <= subtotal;
      $('[data-kit-mrp]', this).textContent = formatMoneyShort(mrp);
      $('[data-kit-subtotal]', this).textContent = formatMoneyShort(subtotal);
      const row = $('[data-kit-disc-row]', this);
      row.hidden = pct === 0;
      $('[data-kit-pct]', this).textContent = pct ? `(${pct}%)` : '';
      $('[data-kit-disc]', this).textContent = `−${formatMoneyShort(disc)}`;
      $('[data-kit-total]', this).textContent = formatMoneyShort(subtotal - disc);
      this.addBtn.disabled = n === 0;
      this.clearBtn.hidden = n === 0;
    }
    async submit() {
      if (!this.count) return;
      this.addBtn.classList.add('is-loading');
      try {
        const items = [];
        this.items.forEach((i, id) => items.push({ id: +id, quantity: i.qty, properties: { _kit: 'true' } }));
        await A.fetchJSON(this.dataset.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        const code = this.dataset.code;
        if (code) { window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(this.dataset.cartUrl)}`; return; }
        this.items.clear(); this.render();
        this.addBtn.classList.remove('is-loading');
        if (A.settings.cartType === 'drawer' && document.querySelector('[data-cart-drawer]')) {
          // refresh drawer contents then open
          const res = await A.fetchJSON(`${A.routes.root}?sections=cart-drawer`);
          const next = A.parseHTML(res['cart-drawer']).querySelector('[data-cart-drawer-content]');
          const cur = document.querySelector('[data-cart-drawer-content]');
          if (next && cur) { cur.replaceWith(next); }
          const cart = await A.cart.get();
          $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.dataset.count = cart.item_count; });
          A.openCartDrawer(this.addBtn);
        } else window.location.href = this.dataset.cartUrl;
      } catch (err) {
        this.addBtn.classList.remove('is-loading');
        toast(err.message || A.strings.error);
      }
    }
  }
  define('bundle-builder', BundleBuilder);
})();
